import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { CashReceiptDetail } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import { CustomerLink } from '@/components/accounts-receivable/customer-link';
import { InvoicedCustomers } from '@/components/customer/InvoicedCustomers';
import { useOverrideCashReceiptCustomer } from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import { useToast } from 'src/context/ToastContext';

function ConfidenceBadge({
  confidence,
}: {
  confidence: string | null | undefined;
}) {
  if (confidence === 'high') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-800 border-green-200"
      >
        <T>High</T>
      </Badge>
    );
  }
  if (confidence === 'medium') {
    return (
      <Badge
        variant="outline"
        className="bg-yellow-50 text-yellow-800 border-yellow-200"
      >
        <T>Medium</T>
      </Badge>
    );
  }
  if (confidence === 'low') {
    return (
      <Badge
        variant="outline"
        className="bg-orange-50 text-orange-800 border-orange-200"
      >
        <T>Low</T>
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      <T>None</T>
    </Badge>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** Asks whether to re-match the receipt against waiting remittances after a change. */
function RematchPrompt({
  pending,
  onRematch,
  onSkip,
}: {
  pending: boolean;
  onRematch: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
      <p className="text-xs text-foreground">
        <T>Customer updated. Re-match with waiting remittances?</T>
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={onRematch}
          disabled={pending}
        >
          {pending ? (
            <>
              <Spinner className="mr-1.5 h-3 w-3" />
              <T>Re-matching...</T>
            </>
          ) : (
            <T>Re-match</T>
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={onSkip}
          disabled={pending}
        >
          <T>Skip</T>
        </Button>
      </div>
    </div>
  );
}

interface CashReceiptCustomerSectionProps {
  receipt: CashReceiptDetail;
}

export function CashReceiptCustomerSection({
  receipt,
}: CashReceiptCustomerSectionProps) {
  const gt = useGT();
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showRematchPrompt, setShowRematchPrompt] = useState(false);
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(
    null,
  );
  const { showToast } = useToast();
  const overrideMutation = useOverrideCashReceiptCustomer();
  const showError = (title: string, err: unknown, fallback: string) =>
    showToast({
      title,
      message: errorMessage(err, fallback),
      color: 'error',
      icon: 'warning',
    });

  async function handleCustomerSelect(customerId: string | undefined) {
    if (!customerId) return;

    try {
      await overrideMutation.mutateAsync({
        receiptId: receipt.id,
        customerId,
        triggerRematch: false,
      });
      setShowAutocomplete(false);
      setPendingCustomerId(customerId);
      setShowRematchPrompt(true);
    } catch (err) {
      showError(gt('Override failed'), err, gt('Failed to override customer'));
    }
  }

  async function handleRematch() {
    if (!pendingCustomerId) return;

    try {
      await overrideMutation.mutateAsync({
        receiptId: receipt.id,
        customerId: pendingCustomerId,
        triggerRematch: true,
      });
      setShowRematchPrompt(false);
      setPendingCustomerId(null);
      showToast({
        title: gt('Re-match complete'),
        message: gt('Receipt re-matched with waiting remittances.'),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      showError(gt('Re-match failed'), err, gt('Re-match failed'));
    }
  }

  function handleSkipRematch() {
    setShowRematchPrompt(false);
    setPendingCustomerId(null);
    showToast({
      title: gt('Customer updated'),
      message: gt('Customer override applied.'),
      color: 'success',
      icon: 'check',
    });
  }

  const { customerMatch } = receipt;
  const hasCustomer = Boolean(receipt.customerId);

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt detail section heading: the customer the bank line was matched to, who sent the money">
          Payer
        </T>
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        {hasCustomer && receipt.customerId ? (
          <CustomerLink
            customerId={receipt.customerId}
            customerName={receipt.customerName}
            erpDisplayName={receipt.erpDisplayName}
          />
        ) : (
          <span className="text-sm text-muted-foreground">
            <T context="AR cash receipt detail, Payer section: the bank line has not been matched to the customer who sent the money">
              No payer matched
            </T>
          </span>
        )}
        <ConfidenceBadge confidence={customerMatch.confidence} />
        {!showAutocomplete && !showRematchPrompt && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAutocomplete(true)}
            className="h-6 px-2 text-xs"
          >
            <T>Change</T>
          </Button>
        )}
      </div>

      {showAutocomplete && (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <CustomersAutocomplete
              selectedCustomerId={receipt.customerId ?? undefined}
              onCustomerSelect={handleCustomerSelect}
              allowClear={false}
              className="w-full"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAutocomplete(false)}
            disabled={overrideMutation.isPending}
            className="h-8 px-2 text-xs"
          >
            <T>Cancel</T>
          </Button>
        </div>
      )}

      {overrideMutation.isPending && !showRematchPrompt && (
        <p className="text-xs text-muted-foreground">
          <T>Updating customer...</T>
        </p>
      )}

      {showRematchPrompt && (
        <RematchPrompt
          pending={overrideMutation.isPending}
          onRematch={handleRematch}
          onSkip={handleSkipRematch}
        />
      )}

      <Accordion type="single" collapsible>
        <AccordionItem value="reasoning" className="border-none">
          <AccordionTrigger className="py-1 text-xs font-medium text-muted-foreground hover:no-underline">
            <T>AI Reasoning</T>
          </AccordionTrigger>
          <AccordionContent className="space-y-3">
            {customerMatch.reasoning ? (
              <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">
                {customerMatch.reasoning}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                <T>No AI reasoning available</T>
              </p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/**
 * Whose invoices the receipt pays, from its linked remittances — distinct from
 * the payer above: one check can settle invoices billed to sister companies.
 * Lists every one (most cash first, as the backend orders them) rather than
 * folding into `+N` like the list view.
 */
export function CashReceiptInvoicedCustomersSection({
  receipt,
}: CashReceiptCustomerSectionProps) {
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt detail section heading: customers whose invoices the receipt's remittances pay">
          Invoiced Customers
        </T>
      </p>
      <InvoicedCustomers
        variant="list"
        className="text-sm"
        customers={receipt.invoicedCustomers}
        fallback={
          <p className="text-sm text-muted-foreground">
            {receipt.hasRemittance ? (
              <T context="AR cash receipt detail: a remittance is linked but none of its lines is matched to an invoice yet, so it is not yet known whose invoices the receipt pays">
                Remittance linked — invoices not matched yet
              </T>
            ) : (
              <T context="AR cash receipt detail: no remittance is linked, so it is not yet known whose invoices the receipt pays">
                No remittance linked yet
              </T>
            )}
          </p>
        }
      />
    </div>
  );
}
